import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../app/app.environment';

export interface PricingGroup {
  id: number;
  name: string;
}

export interface Customer {
  id: number;
  customer_name: string;
  mobile_no: string;
  sellingpricegroup_id: number | null;
  address_line1: string;
  address_line2: string;
  address_line3: string;
  city: string;
  state: string;
  country: string;
  zip_code: string;
  pan_no?: string;
  gst_no?: string;
  contact_person?: string;
  phone_no?: string;
  fax_no?: string;
  email?: string;
}

export interface PricingGroupResponse {
  status: number;
  message: string;
  data: PricingGroup[];
}

export interface CustomerResponse {
  status: number;
  message: string;
  data: Customer[];
}

@Injectable({
  providedIn: 'root'
})
export class OfferGroupService {
  private apiUrl = environment.apiURL + 'v1';
  
  private selectedGroupSubject = new BehaviorSubject<number | null>(null);
  public selectedGroup$ = this.selectedGroupSubject.asObservable();
  
  private isOfferGroupModeSubject = new BehaviorSubject<boolean>(false);
  public isOfferGroupMode$ = this.isOfferGroupModeSubject.asObservable();

  constructor(private http: HttpClient) {}

  // Fetch pricing groups from API
  getPricingGroups(): Observable<PricingGroup[]> {
    return this.http.get<PricingGroupResponse>(`${this.apiUrl}/order/pricingrouplist`)
      .pipe(
        map(response => response.data || [])
      );
  }

  // Fetch customers from API
  getCustomers(): Observable<Customer[]> {
    return this.http.get<CustomerResponse>(`${this.apiUrl}/order/getcustomer`)
      .pipe(
        map(response => response.data || [])
      );
  }

  // Set selected pricing group
  setSelectedGroup(groupId: number | null): void {
    this.selectedGroupSubject.next(groupId);
  }

  // Get current selected group
  getSelectedGroup(): number | null {
    return this.selectedGroupSubject.value;
  }

  // Enable/disable offer group mode
  setOfferGroupMode(enabled: boolean): void {
    this.isOfferGroupModeSubject.next(enabled);
    if (!enabled) {
      this.setSelectedGroup(null);
    }
  }

  // Get current offer group mode status
  isOfferGroupMode(): boolean {
    return this.isOfferGroupModeSubject.value;
  }

  // Find customer by mobile number
  findCustomerByMobile(customers: Customer[], mobile: string): Customer | null {
    return customers.find(customer => customer.mobile_no === mobile) || null;
  }

  // Get pricing group name by ID
  getPricingGroupName(groups: PricingGroup[], groupId: number): string {
    const group = groups.find(g => g.id === groupId);
    return group ? group.name : '';
  }
}
